import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import { toast } from "react-toastify";
import NavHeader from "../shared/components/NavHeader";
import EventButton, {
  EventButtonVariant,
} from "../shared/components/EventButton";
import PInput from "../../../ui/input/PInput";
import PErrors from "../../../ui/input/PErrors";
import GuestListStore from "./store/guestListStore";
import EventStore from "../event/store/eventStore";

interface GuestAddPageProps {
  onBack: () => void;
}

const GuestAddPageComponent: React.FC<GuestAddPageProps> = ({ onBack }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [firstNameErrors, setFirstNameErrors] = useState<string[]>([]);
  const [lastNameErrors, setLastNameErrors] = useState<string[]>([]);
  const [phoneErrors, setPhoneErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validatePhoneNumber = (phoneNumber: string): boolean => {
    // Remove all non-numeric characters for validation
    const cleanedPhone = phoneNumber.replace(/\D/g, "");

    // Check if it's a valid US phone number (10 digits, optionally with 1 prefix)
    if (cleanedPhone.length === 10) {
      return true;
    }
    if (cleanedPhone.length === 11 && cleanedPhone.startsWith("1")) {
      return true;
    }
    return false;
  };

  const formatPhoneNumber = (phoneNumber: string): string => {
    // Remove all non-numeric characters
    const cleanedPhone = phoneNumber.replace(/\D/g, "");

    // If it's 10 digits, add US country code
    if (cleanedPhone.length === 10) {
      return `+1${cleanedPhone}`;
    }
    // If it's 11 digits starting with 1, add plus sign
    if (cleanedPhone.length === 11 && cleanedPhone.startsWith("1")) {
      return `+${cleanedPhone}`;
    }
    // Return original if it doesn't match expected format
    return phoneNumber;
  };

  const validateForm = (): boolean => {
    let isValid = true;
    const newFirstNameErrors: string[] = [];
    const newLastNameErrors: string[] = [];
    const newPhoneErrors: string[] = [];

    // Validate first name
    if (!firstName.trim()) {
      newFirstNameErrors.push("First name is required");
      isValid = false;
    } else if (firstName.trim().length < 2) {
      newFirstNameErrors.push("First name must be at least 2 characters");
      isValid = false;
    }

    // Validate last name
    if (!lastName.trim()) {
      newLastNameErrors.push("Last name is required");
      isValid = false;
    } else if (lastName.trim().length < 2) {
      newLastNameErrors.push("Last name must be at least 2 characters");
      isValid = false;
    }

    // Validate phone number
    if (!phone.trim()) {
      newPhoneErrors.push("Phone number is required");
      isValid = false;
    } else if (!validatePhoneNumber(phone)) {
      newPhoneErrors.push("Please enter a valid US phone number (10 digits)");
      isValid = false;
    }

    setFirstNameErrors(newFirstNameErrors);
    setLastNameErrors(newLastNameErrors);
    setPhoneErrors(newPhoneErrors);

    return isValid;
  };

  const handleAddGuest = async () => {
    // Clear previous errors
    setFirstNameErrors([]);
    setLastNameErrors([]);
    setPhoneErrors([]);

    if (!validateForm()) {
      return;
    }

    if (!EventStore.sitchEventData?.guid) {
      toast.error("Event information is missing. Please try again.");
      return;
    }

    setIsSubmitting(true);
    try {
      const formattedPhone = formatPhoneNumber(phone);

      await GuestListStore.addGuest({
        event: EventStore.sitchEventData.guid,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: formattedPhone,
      });

      await toast.success("Guest added successfully!");

      // Clear form
      setFirstName("");
      setLastName("");
      setPhone("");
      setFirstNameErrors([]);
      setLastNameErrors([]);
      setPhoneErrors([]);
    } catch (error) {
      toast.error("Failed to add guest. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <NavHeader onBack={onBack} />

      <div className="max-w-md mx-auto px-6 pt-6 pb-4">
        <div className="flex mb-6 relative">
          <h1 className="text-4xl font-nostalgic">ADD NEW GUEST</h1>
        </div>
      </div>

      <div className="max-w-md mx-auto px-6 pt-6 pb-4">
        <div className="mb-4">
          <p className="block text-sm font-medium mb-2">First Name</p>
          <PInput
            type="text"
            placeholder="John"
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              // Clear error when user starts typing
              if (firstNameErrors.length > 0) {
                setFirstNameErrors([]);
              }
            }}
            className={`w-full ${firstNameErrors.length > 0 ? "border-red-500" : ""}`}
            onEnterPressed={handleAddGuest}
          />
          <PErrors errors={firstNameErrors} />
        </div>

        <div className="mb-4">
          <p className="block text-sm font-medium mb-2">Last Name</p>
          <PInput
            type="text"
            placeholder="Doe"
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              // Clear error when user starts typing
              if (lastNameErrors.length > 0) {
                setLastNameErrors([]);
              }
            }}
            className={`w-full ${lastNameErrors.length > 0 ? "border-red-500" : ""}`}
            onEnterPressed={handleAddGuest}
          />
          <PErrors errors={lastNameErrors} />
        </div>

        <div className="mb-4">
          <p className="block text-sm font-medium mb-2">Phone Number</p>
          <PInput
            id="phone-number"
            type="tel"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              // Clear error when user starts typing
              if (phoneErrors.length > 0) {
                setPhoneErrors([]);
              }
            }}
            placeholder="(555) 123-4567 or 5551234567"
            className={`w-full ${phoneErrors.length > 0 ? "border-red-500" : ""}`}
            onEnterPressed={handleAddGuest}
          />
          <PErrors errors={phoneErrors} />
          <p className="text-xs text-gray-400 mt-1">
            Enter a US phone number (10 digits)
          </p>
        </div>

        <EventButton
          onClick={handleAddGuest}
          variant={EventButtonVariant.PRIMARY_WHITE}
          className="mt-6 w-full mb-4"
          icon="➕"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Adding..." : "Add guest"}
        </EventButton>
      </div>
    </div>
  );
};

export const GuestAddPage = observer(GuestAddPageComponent);

export default GuestAddPage;
